import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import uniqBy from "lodash/uniqBy";
import { useState } from "react";
import {
  UseFieldArrayInsert,
  UseFieldArrayRemove,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { CreateProductLineDto } from "#modules/productLines/infra/createProductLine.api";
import { useCreateProductLine } from "#modules/productLines/view/useCreateProductLine";
import { useDeleteProductLine } from "#modules/productLines/view/useDeleteProductLine";
import { useUpdateProductLine } from "#modules/productLines/view/useUpdateProductLine";
import { productsQuery } from "#modules/products/infra/products.query";
import { useProducts } from "#modules/products/view/useProducts";
import { quoteRequestLineQuery } from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";
import {
  EDITING_ACTION_SPAN,
  EDITING_QUANTITY_SPAN,
  IDLE_QUANTITY_SPAN,
  PRICE_SPAN,
  PRODUCT_NAME_SPAN,
  PRODUCT_TABLE_COLUMS_COUNT,
  TRASH_SPAN,
} from "#modules/quoteRequestLines/views/DetailsPanel/Products/Products";
import { useProductLineStatus } from "#modules/quoteRequestLines/views/DetailsPanel/Products/useProductLineStatus";
import { Icon } from "#shared/icons/Icon";
import { ActionIcon } from "#shared/view/components/ActionIcon";
import { Flex } from "#shared/view/components/Flex";
import { Grid } from "#shared/view/components/Grid";
import { InlineLoader } from "#shared/view/components/InlineLoader";
import { PriceInputController } from "#shared/view/components/PriceInputController";
import { Button } from "#shared/view/designSystem/Button";
import { NumberInputController } from "#shared/view/designSystem/inputs/NumberInputController";
import { SelectInputController } from "#shared/view/designSystem/inputs/SelectInputController";
import { ProductTypeSchema } from "shared";

export const Product = ({
  index,
  removeProduct,
  insertProduct,
  productLineId,
}: {
  index: number;
  removeProduct: UseFieldArrayRemove;
  insertProduct: UseFieldArrayInsert<QuoteRequestLineFormData, "productLines">;
  productLineId: string | undefined;
}) => {
  const queryClient = useQueryClient();
  const { control, resetField, setValue } = useFormContext<QuoteRequestLineFormData>();

  const currentProductLine = useWatch({ control, name: `productLines.${index}` as const });

  const { products } = useProducts();
  const { deleteProductLine } = useDeleteProductLine({
    index,
    insertProduct,
    removeProduct,
  });

  const deleteProductHandler = () => {
    deleteProductLine({ id: productLineId });
  };

  const quoteRequestLineId = useWatch({ control, name: "quoteRequestLineId" });

  const { updateProductLine, isPending } = useUpdateProductLine({
    quoteRequestLineId: quoteRequestLineId ?? "",
    options: {
      onSuccess: () => {
        resetField(`productLines.${index}`);
      },
    },
  });

  const { createProductLine, isPending: isCreating } = useCreateProductLine({
    quoteRequestLineId: quoteRequestLineId ?? "",
    options: {
      onSuccess: () => {
        void queryClient.invalidateQueries(quoteRequestLineQuery({ id: quoteRequestLineId }));
        void queryClient.invalidateQueries(productsQuery());
        resetField(`productLines.${index}`);
      },
    },
  });

  const { isDirty, isSavable, isNew } = useProductLineStatus(index, productLineId);

  const resetLineHandler = () => {
    if (isNew) {
      removeProduct(index);
    } else {
      resetField(`productLines.${index}`);
    }
  };

  const saveLineHandler = () => {
    const payload: CreateProductLineDto & {
      productId: string | undefined;
    } = {
      productId: currentProductLine?.product?.id ?? undefined,
      quantity: currentProductLine?.quantity ?? 0,
      isQuantityPerPart: true,
      name: currentProductLine?.name ?? "",
      price: currentProductLine?.price ?? 0,
      fixedPrice: currentProductLine?.fixedPrice ?? 0,
      product: {
        name: currentProductLine?.name ?? "",
        price: currentProductLine?.price ?? 0,
        fixedPrice: currentProductLine?.fixedPrice ?? 0,
        type: ProductTypeSchema.enum.CONSUMABLE,
      },
    };

    if (isNew) {
      createProductLine(payload);
    } else {
      if (isSavable && productLineId) {
        updateProductLine({
          id: productLineId,
          payload,
        });
      }
    }
  };

  const isSaving = isPending || isCreating;
  const isIdle = !isDirty;
  const isEditing = !isSaving && isDirty;

  const addNewProduct = (name: string) => {
    createProductLine({
      name,
      price: 0,
      fixedPrice: 0,
      isQuantityPerPart: true,
      quantity: 1,
      product: {
        name,
        price: 0,
        fixedPrice: 0,
        type: ProductTypeSchema.enum.CONSUMABLE,
      },
    });
  };

  const [searchTerm, setSearchTerm] = useState("");
  const onSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const onSelectExistingProduct = (value: string) => {
    const product = products.find((p) => p.name === value);

    if (product) {
      setValue(`productLines.${index}.price`, product.price);
      setValue(`productLines.${index}.fixedPrice`, product.fixedPrice);
    }
  };

  return (
    <Grid columns={PRODUCT_TABLE_COLUMS_COUNT} gap={0} align="center">
      <Grid.Col span={TRASH_SPAN}>
        <Flex justify="start" align="center">
          {!isNew && (
            <ActionIcon size="xs" onClick={deleteProductHandler}>
              <Icon name="trash" size={14} />
            </ActionIcon>
          )}
        </Flex>
      </Grid.Col>
      <Grid.Col span={PRODUCT_NAME_SPAN}>
        <SelectInputController
          data={uniqBy(
            products.map((product) => ({
              label: product.name,
              value: product.name,
            })),
            "value",
          )}
          size="xs"
          name={`productLines.${index}.name`}
          control={control}
          placeholder={t("quoteRequestLine.purchases.productName")}
          required
          searchable
          selectFirstOptionOnChange
          onSearchChange={onSearchChange}
          onChange={onSelectExistingProduct}
          nothingFoundMessage={
            <Button onClick={() => addNewProduct(searchTerm)} variant="subtle">
              {t("product.add", { name: searchTerm })}
            </Button>
          }
        />
      </Grid.Col>
      <Grid.Col span={PRICE_SPAN}>
        <PriceInputController
          size="xs"
          name={`productLines.${index}.fixedPrice`}
          defaultValue={0}
          control={control}
        />
      </Grid.Col>
      <Grid.Col span={PRICE_SPAN}>
        <PriceInputController
          size="xs"
          name={`productLines.${index}.price`}
          control={control}
          required
        />
      </Grid.Col>

      <Grid.Col span={isIdle ? IDLE_QUANTITY_SPAN : EDITING_QUANTITY_SPAN}>
        <NumberInputController
          size="xs"
          name={`productLines.${index}.quantity`}
          control={control}
          precision={2}
          required
        />
      </Grid.Col>

      {isSaving && (
        <Grid.Col
          span={EDITING_ACTION_SPAN}
          style={{ display: "flex", gap: 4, justifyContent: "end" }}
        >
          <InlineLoader size="xs" color={"violet"} />
        </Grid.Col>
      )}
      {isEditing && (
        <Grid.Col span={EDITING_ACTION_SPAN} style={{ display: "flex", gap: 4 }}>
          <ActionIcon
            type="submit"
            variant="primary"
            bg={isSavable ? "green" : "gray.2"}
            disabled={!isSavable}
            onClick={saveLineHandler}
          >
            <Icon name="check" size={14} />
          </ActionIcon>
          <ActionIcon type="submit" variant="outline" onClick={resetLineHandler}>
            <Icon name="x" color="red" size={14} />
          </ActionIcon>
        </Grid.Col>
      )}
    </Grid>
  );
};
